// Marketing (admins + managers): emails to customers — promotion announcements and reminders,
// lookbooks, and general news — plus the lookbook PDF builder and the unsubscribe list.
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader.jsx';
import Lookbooks from './Lookbooks.jsx';
import { fetchCampaigns, createCampaign, fetchSuppressions, duplicateCampaign, deleteCampaign, fetchTemplates } from '../api/marketing.js';
import { fetchDiscounts } from '../api/discounts.js';
import { storeShort } from '../lib/stores.js';
import { useAuthStore } from '../stores/authStore.js';
import { allowedStoreCodes } from '../lib/access.js';
import { promoStatus } from './Discounts.jsx';

const errMsg = (e) => e.response?.data?.error?.message || 'Something went wrong.';
const chip = (on) => `rounded-full px-3 py-1 text-sm ${on ? 'bg-accent text-white' : 'bg-surface-raised text-text-secondary hover:text-text-primary'}`;
export const TYPE_LABEL = { PROMOTION: 'Promotion announcement', REMINDER: 'Promotion reminder', LOOKBOOK: 'Lookbook', GENERAL: 'Email' };
const STATUS = {
  DRAFT: ['Draft', 'bg-surface-raised text-text-secondary'],
  SCHEDULED: ['Scheduled', 'bg-accent-light text-accent'],
  SENDING: ['Sending…', 'bg-warning-light text-warning'],
  TESTING: ['A/B testing', 'bg-info-light text-info'],
  SENT: ['Sent', 'bg-success-light text-success'],
  FAILED: ['Failed', 'bg-error-light text-error'],
};
const when = (d) => (d ? new Date(d).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '');

// "New email" chooser: pick a type (and the promotion for announcements / reminders).
function NewEmail({ onCancel, onCreated }) {
  const user = useAuthStore((s) => s.user);
  const stores = allowedStoreCodes(user) || ['A', 'B'];
  const [type, setType] = useState('PROMOTION');
  const [store, setStore] = useState(stores[0]);
  const [promos, setPromos] = useState([]);
  const [discountId, setDiscountId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    fetchDiscounts()
      .then((l) => setPromos(l.filter((d) => !d.archivedAt && ['live', 'scheduled'].includes(promoStatus(d).key))))
      .catch(() => setPromos([]));
    fetchTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, []);
  const needsPromo = type === 'PROMOTION' || type === 'REMINDER';
  const go = async () => {
    setBusy(true);
    setError('');
    try {
      onCreated(await createCampaign(needsPromo ? { type, discountId } : { type, store, ...(type === 'GENERAL' && templateId ? { templateId } : {}) }));
    } catch (e) {
      setError(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <div className="mb-4 rounded-lg border border-accent/40 bg-surface p-5 shadow-sm">
      <h2 className="text-sm font-semibold text-text-primary">New email</h2>
      <div className="mt-3 grid gap-2 sm:grid-cols-4">
        {[
          ['PROMOTION', 'Announce a promotion', 'Tell the targeted clients about a new offer'],
          ['REMINDER', 'Promotion reminder', 'Nudge clients who haven’t used it yet'],
          ['LOOKBOOK', 'Lookbook', 'Share a collection as a PDF lookbook'],
          ['GENERAL', 'Design an email', 'News, launches, newsletters — blank or from a template'],
        ].map(([k, title, hint]) => (
          <button key={k} type="button" onClick={() => setType(k)} className={`rounded-md border p-3 text-left ${type === k ? 'border-accent bg-accent-light' : 'border-line hover:bg-surface-raised'}`}>
            <div className="text-sm font-medium text-text-primary">{title}</div>
            <div className="mt-0.5 text-xs text-text-muted">{hint}</div>
          </button>
        ))}
      </div>
      {needsPromo ? (
        <label className="mt-3 block max-w-md text-xs font-medium text-text-muted">
          Promotion
          <select value={discountId} onChange={(e) => setDiscountId(e.target.value)} className="mt-1 w-full rounded-md border border-line bg-surface px-3 py-2 text-sm">
            <option value="">Choose a live or scheduled promotion…</option>
            {promos.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} · {storeShort(d.store)}
              </option>
            ))}
          </select>
        </label>
      ) : (
        stores.length > 1 && (
          <div className="mt-3 flex items-center gap-2 text-xs font-medium text-text-muted">
            Customers of
            {stores.map((s) => (
              <button key={s} type="button" onClick={() => setStore(s)} className={chip(store === s)}>{storeShort(s)}</button>
            ))}
          </div>
        )
      )}
      {type === 'GENERAL' && templates.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-medium text-text-muted">Start from</div>
          <div className="mt-1.5 grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {[{ id: '', name: 'Blank', description: 'A heading, a note and a button.' }, ...templates].map((t) => (
              <button key={t.id || 'blank'} type="button" onClick={() => setTemplateId(t.id)} className={`rounded-md border p-2.5 text-left ${templateId === t.id ? 'border-accent bg-accent-light' : 'border-line hover:bg-surface-raised'}`}>
                <div className="text-sm font-medium text-text-primary">{t.name}</div>
                <div className="mt-0.5 line-clamp-2 text-[11px] text-text-muted">{t.description || (t.builtIn ? '' : 'Saved template')}</div>
              </button>
            ))}
          </div>
        </div>
      )}
      {error && <p className="mt-2 text-sm text-error">{error}</p>}
      <div className="mt-4 flex gap-2">
        <button onClick={go} disabled={busy || (needsPromo && !discountId)} className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50">
          {busy ? 'Setting up…' : 'Start writing'}
        </button>
        <button onClick={onCancel} className="rounded-md border border-line px-3 py-2 text-sm font-medium text-text-secondary hover:bg-surface-raised">Cancel</button>
      </div>
      {needsPromo && <p className="mt-2 text-xs text-text-muted">The subject and copy are drafted for you from the promotion — you review everything before it’s sent.</p>}
    </div>
  );
}

function CampaignList({ campaigns, onOpen, onChanged }) {
  const [tab, setTab] = useState('drafts');
  const groups = {
    drafts: campaigns.filter((c) => ['DRAFT', 'FAILED'].includes(c.status)),
    scheduled: campaigns.filter((c) => ['SCHEDULED', 'SENDING'].includes(c.status)),
    sent: campaigns.filter((c) => c.status === 'SENT'),
  };
  const shown = groups[tab];
  return (
    <>
      <div className="mb-3 flex gap-2">
        {[
          ['drafts', 'Drafts'],
          ['scheduled', 'Scheduled'],
          ['sent', 'Sent'],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={chip(tab === k)}>
            {l}
            {groups[k].length ? ` (${groups[k].length})` : ''}
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-sm text-text-muted">Nothing here yet.</div>
      ) : (
        <div className="divide-y divide-line rounded-lg border border-line bg-surface shadow-sm">
          {shown.map((c) => {
            const [label, cls] = STATUS[c.status] || [c.status, ''];
            return (
              <div key={c.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <button onClick={() => onOpen(c.id)} className="min-w-0 flex-1 text-left">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-text-primary">{c.name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>
                    <span className="rounded-full bg-surface-raised px-2 py-0.5 text-xs text-text-secondary">{storeShort(c.store)}</span>
                  </div>
                  <div className="mt-0.5 truncate text-sm text-text-secondary">{c.subject || <span className="text-text-muted">No subject yet</span>}</div>
                  <div className="mt-0.5 text-xs text-text-muted">
                    {TYPE_LABEL[c.type]}
                    {c.discountName ? ` · ${c.discountName}` : ''}
                    {c.status === 'SENT' && ` · sent ${when(c.sentAt)} to ${c.stats.sent} · ${c.stats.sent ? Math.round((c.stats.opened / c.stats.sent) * 100) : 0}% opened`}
                    {c.status === 'SCHEDULED' && ` · sends ${when(c.scheduledAt)}`}
                    {c.status === 'FAILED' && c.lastError ? ` · ${c.lastError}` : ''}
                    {['DRAFT'].includes(c.status) && ` · edited ${when(c.updatedAt)}${c.createdBy ? ` by ${c.createdBy}` : ''}`}
                  </div>
                </button>
                <div className="flex shrink-0 gap-3 text-sm">
                  <button onClick={() => duplicateCampaign(c.id).then((d) => onOpen(d.id))} className="text-text-secondary hover:text-text-primary">Duplicate</button>
                  {['DRAFT', 'FAILED'].includes(c.status) && (
                    <button onClick={() => window.confirm(`Delete the draft “${c.name}”?`) && deleteCampaign(c.id).then(onChanged)} className="text-text-secondary hover:text-error">Delete</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function Unsubscribes() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    fetchSuppressions().then(setRows).catch(() => setRows([]));
  }, []);
  if (!rows) return <div className="text-sm text-text-muted">Loading…</div>;
  const reason = { unsubscribed: 'Unsubscribed', bounced: 'Address bounced', complained: 'Marked as spam' };
  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
      <p className="text-sm text-text-secondary">These addresses don’t receive marketing emails. Invoices, sign-in codes and order emails still reach them.</p>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-text-muted">No one has unsubscribed.</p>
      ) : (
        <table className="mt-3 w-full text-sm">
          <tbody className="divide-y divide-line">
            {rows.map((r) => (
              <tr key={r.email}>
                <td className="py-1.5 text-text-primary">{r.email}</td>
                <td className="py-1.5 text-text-secondary">{reason[r.reason] || r.reason}</td>
                <td className="py-1.5 text-right text-text-muted">{new Date(r.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Marketing() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'emails';
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const load = () => {
    setLoading(true);
    fetchCampaigns().then(setCampaigns).catch(() => setCampaigns([])).finally(() => setLoading(false));
  };
  useEffect(load, []);
  const open = (id) => navigate(`/marketing/${id}`);

  return (
    <>
      <PageHeader
        title="Marketing"
        subtitle="Emails to your wholesale customers — promotion announcements and reminders, lookbooks and news. Each client gets a personal email from their account contact."
        actions={
          tab === 'emails' && !creating ? (
            <button onClick={() => setCreating(true)} className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-hover">New email</button>
          ) : null
        }
      />
      <div className="mb-4 flex gap-2 border-b border-line">
        {[
          ['emails', 'Emails'],
          ['lookbook', 'Lookbook PDF'],
          ['unsubscribes', 'Unsubscribes'],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setParams(k === 'emails' ? {} : { tab: k })} className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${tab === k ? 'border-accent text-text-primary' : 'border-transparent text-text-muted hover:text-text-primary'}`}>
            {l}
          </button>
        ))}
      </div>
      {tab === 'emails' && (
        <>
          {creating && <NewEmail onCancel={() => setCreating(false)} onCreated={(c) => open(c.id)} />}
          {loading ? <div className="text-sm text-text-muted">Loading…</div> : <CampaignList campaigns={campaigns} onOpen={open} onChanged={load} />}
        </>
      )}
      {tab === 'lookbook' && (
        <>
          <p className="mb-3 text-sm text-text-secondary">Build and download a branded PDF lookbook. To email one to clients, start a <button onClick={() => { setParams({}); setCreating(true); }} className="font-medium text-accent hover:underline">Lookbook email</button>.</p>
          <Lookbooks embedded />
        </>
      )}
      {tab === 'unsubscribes' && <Unsubscribes />}
    </>
  );
}
